/**
 * Company: SPHMedia
 * Description: The basic article card
 */

import { ReactElement } from "react";
import { type ArticleCardProps } from "@components/ArticleCard/types";
import CreateTime from "@components/ArticleMeta/CreateTime";
import SubscriberFlag from "@components/ArticleMeta/SubscriberFlag";
import Summary from "@components/ArticleMeta/Summary";
import TitleLink from "@components/ArticleMeta/TitleLink";
import UpdateTime from "@components/ArticleMeta/UpdateTime";
import ArticleThumbnail from "@components/ArticleThumbnail/ArticleThumbnail";
import cx from "classnames";

import SectionLink from "../ArticleMeta/SectionLink";

export interface BasicArticleCardProps extends ArticleCardProps {
  showSection?: boolean;
  showImage?: boolean;
  imagePosition?: "left" | "right" | "";
}

/**
 * BasicArticleCard
 * @description The basic article card
 * @param {BasicArticleCardProps} props
 * @returns {ReactElement}
 */
export default function BasicArticleCard({
  id = "",
  rootClassName = "",
  articleData,
  showSection = true,
  showImage = true,
  imagePosition = "",
}: BasicArticleCardProps): ReactElement {
  const {
    id: articleId = "",
    slug,
    sections = [],
    created,
    updated,
    edited,
    title,
    paywall,
    media,
    displaySetting,
  } = articleData;
  const componentId = id || `article_${articleId}`;
  const _media = media && "length" in media ? media.at(0) : media;
  const previewText = _media?.summary?.[0]?.value; // !need to confirm with OS's return for this

  let imageWrapperClass = "";
  let contentWrapperClass = "";

  switch (imagePosition) {
    case "left":
      imageWrapperClass = "lg:order-1";
      contentWrapperClass = "lg:order-2 pl-4";
      break;
    case "right":
      imageWrapperClass = "lg:order-2";
      contentWrapperClass = "lg:order-1 pl-4 lg:pl-0 lg:pr-4";
      break;
    default:
      imageWrapperClass = "";
      contentWrapperClass = _media?.content ? "pl-4" : "";
      break;
  }

  return (
    <div
      id={componentId}
      data-testid={componentId}
      className={cx(rootClassName, "flex flex-row flex-wrap")}
    >
      {showImage ? (
        <ArticleThumbnail
          rootClassName={cx(imageWrapperClass, "w-120px lg:w-3/20")}
          thumbnail={_media?.content}
          orientation="landscape"
          imgRootClassName="aspect-ratio-3x2"
        />
      ) : null}

      <div
        className={cx(
          "flex w-full-120px flex-col gap-y-1",
          "lg:w-17/20",
          contentWrapperClass
        )}
      >
        {sections && sections.length > 0 && showSection ? (
          <SectionLink
            sectionName={sections.at(0)?.name}
            sectionUniqueName={sections.at(0)?.uniqueName}
          />
        ) : null}

        {title ? (
          <TitleLink
            rootClassName={cx("font-poppins font-semibold text-base")}
            articleId={slug ?? articleId}
            title={title}
          />
        ) : null}

        {paywall && paywall.contentAccess === "1" ? <SubscriberFlag /> : null}

        {previewText ? (
          <Summary
            rootClassName="!font-poppins !text-[14px] !font-normal"
            summary={previewText}
          />
        ) : null}

        <div>
          {created ? <CreateTime created={created} /> : null}

          {displaySetting?.hasUpdatedTimeDisplayed ? (
            <UpdateTime
              updated={updated}
              edited={edited}
              rootClassName="text-red"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
